import { HeroBackground, ProductMain } from '@prishapolicy/shared/ui';
import {
  ChoosePlan,
  ChoosePlanProps,
  Faq,
  getPurchaseFlowHeroForms,
  HeroTicker,
  motorfaqList,
  ProductCategoryPageProps,
  productsPathAdder,
  ProtectToday,
  useW3PurchaseFlow,
  VerticalCardsView,
  WhyChooseUs,
} from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import CarWashingFillIcon from 'remixicon-react/CarWashingFillIcon';
import RestartFillIcon from 'remixicon-react/RestartFillIcon';
import TeamFillIcon from 'remixicon-react/TeamFillIcon';

const productCategorySlug = 'motor-insurance';

export function MotorInsurance(props: ProductCategoryPageProps) {
  const choosePlanCards: ChoosePlanProps['cards'] = [
    {
      title: 'Look for Comprehensive Cover ',
      content: (
        <div>
          While third-party coverage is essential for driving, it is not enough. Comprehensive coverage includes
          protection from both natural and man-made disasters like storms, earthquakes, theft, and mob unrest. Also,
          check for cashless facilities in these plans or the ease of not paying money upfront.
        </div>
      ),
    },
    {
      title: 'Check Insured Declared Valued',
      content: (
        <div>
          IDV is the claim amount that the insurer will pay in the event of an accident or when it requires a repair
          covered by the policy. Based on your vehicle, ensure to forecast expenses and calculate how much IDV you need.
        </div>
      ),
    },
    {
      title: 'Check for<br />Additional Covers ',
      content: (
        <div>
          Additional covers include assistance with processes and services other than monetary payments. They may be
          roadside assistance or zero-depreciation covers, the types of services depends on the insurers you choose.
        </div>
      ),
    },
    {
      title: 'Opt for<br />No Claim Bonus ',
      content: (
        <div>
          This is a reward given by insurers for not making a single claim during an agreed period, usually a year. The
          reward amount is regulated by the IRDAI and increases every period a claim is not made.
        </div>
      ),
    },
  ];
  const description =
    'Quit switching providers every renewal. Find the best insurance for your vehicle with Prisha Policy.';
  const { IconRenderer, FormRenderer } = useW3PurchaseFlow(props);
  // console.log('props:', props);
  return (
    <>
      <NextSeo description={description} title="Motor Insurance | PrishaPolicy" />
      <ProductMain color="red">
        <HeroBackground PurchaseFlowFormRenderer={FormRenderer} backgroundColor="red-400">
          <div className="grid-cols-2 lg:grid ">
            <div className="flex flex-col justify-between">
              <div>
                <h1 className="heading-1 pb-3">Drive safe. Manage driver risk. Drive with Confidence</h1>
                <p className="text-title-6 max-w-[600px]">{description}</p>
              </div>
              <IconRenderer />
            </div>
            <div className="hidden object-fill lg:block">
              <video src={`/products/motor-insurance/animation.webm`} autoPlay playsInline muted loop></video>
            </div>
          </div>
        </HeroBackground>
        <HeroTicker />
        <ProtectToday maxPerRow={5} items={props.productPaths} productCategoryPrefix={productCategorySlug} />
        <div className="container grid-cols-2 gap-x-24 py-24 lg:grid">
          <div className="flex flex-col justify-center">
            <h2 className="lg:heading-3 heading-4">Why is insurance a must-have for a vehicle owner? </h2>
            <p className="prose mt-4">
              Motor insurance policies are designed to protect you from unforeseen expenses concerning your vehicle.
              <br />
              The expenses may come in the form of accidents, recurring repairs, and damage caused by natural disasters.
              It will also provide coverage for mandatory payments related to third-party liabilities.
            </p>
          </div>
          <div className="mt-2 grid place-items-center xl:p-10">
            <Image src={require('./motor-must-have.png')} alt="motor insurance" />
          </div>
          <div className="mb-16 lg:mb-0">
            <h2 className="lg:heading-3 heading-4">Safety for all</h2>
            <p className="mt-3 lg:mt-4">
              Coverage for medical expenses due to a vehicular mishap or accident and the unfortunate demise of a family
              member is also included in some motor insurance policies.
            </p>
          </div>
          <div className="mb-16 lg:mb-0">
            <h2 className="lg:heading-3 heading-4">Mandate by Law</h2>
            <p className="mt-4">
              Under the Indian Motor vehicles act, it is mandatory for all vehicles on Indian roads to have vehicle
              insurance under a Motor Third Party Liability insurance policy.
            </p>
          </div>
          <div className="place-items-center lg:grid xl:p-10">
            <Image src={require('./third-party.png')} alt="motor insurance" />
          </div>
          <div className="flex flex-col justify-center">
            <h2 className="lg:heading-3 heading-4">What is a Motor Third Party Liability insurance policy? </h2>
            <p className="prose mt-4">
              It is a policy that covers the expenses when an accident has occurred, for which the driver or owner of
              the car was responsible.
              <br />
              <br />
              The scheme needs to cover expenses for all those that are damaged by the accident and not just the primary
              owner of the vehicle.
            </p>
          </div>
          <div className="grid place-items-center lg:hidden xl:p-10"></div>
        </div>
        <ChoosePlan perRow={4} cards={choosePlanCards} />
        <div className="mx-auto w-3/4 pt-16 text-center">
          <h2 className="lg:heading-3 heading-4">Different Types of Motor Insurance?</h2>
          <p className="mt-4">
            While third-party coverage is essential for driving, it is not enough. Comprehensive coverage includes
            protection from both natural and man-made disasters like storms, earthquakes, theft, and mob unrest. Also,
            check for cashless facilities in these plans or the ease of not paying money upfront.
          </p>
        </div>
        <VerticalCardsView
          carouselItems={[
            {
              text: 'A comprehensive vehicle insurance policy provides coverage against the third-party legal liabilities as well as the losses or damages to the insured vehicle during an accident or mishap. It covers the vehicle against natural and man-made disasters, including road accidents, fire, theft, earthquake, storm, flood etc.',
              icon: RestartFillIcon,
              heading: 'Comprehensive Motor Insurance',
              rightContent: (
                <div className="hidden place-items-center lg:grid">
                  <Image src={require('./motor-hammer.png')} alt="motor insurance" />
                </div>
              ),
              recommendationText: `Recommended for: Vehicle owners who want all-rounder protection for their vehicle.`,
            },
            {
              text: `Introduced by the IRDAI, the standalone Own Damage (OD) cover policies allow the vehicle owners to get coverage against the losses or damages caused to their vehicle due to natural or man-made disasters. This type of policy can be purchased by those who already have a third-party cover from a different or the same insurer.`,
              icon: CarWashingFillIcon,
              heading: 'Standalone Own Damage cover',
              rightContent: (
                <div className="hidden place-items-center lg:grid">
                  <Image src={require('./motor-tree.png')} alt="motor insurance" />
                </div>
              ),
              recommendationText: `Recommended for: Vehicle owners who want all-rounder protection for their vehicle.`,
            },
            {
              text: `Third-party motor insurance is mandatory in India as per the law. It protects the policyholder against legal liabilities arising due to an accident involving his/her vehicle. It means that in case an insured vehicle causes bodily injuries, death or property damage to a third-party, the insurer will take care of the legal liability.`,
              icon: TeamFillIcon,
              heading: 'Third-Party Motor Insurace',
              rightContent: (
                <div className="hidden place-items-center lg:grid">
                  <Image src={require('./third-party.png')} alt="motor insurance" />
                </div>
              ),
              recommendationText: `Recommended for: Vehicle owners who want all-rounder protection for their vehicle.`,
            },
          ]}
        />
        <WhyChooseUs />
        <Faq list={motorfaqList} />
      </ProductMain>
    </>
  );
}

export function getStaticProps() {
  // protectCardCheck(productCategorySlug, productsPathAdder(productCategorySlug));
  const productPaths = productsPathAdder(productCategorySlug);
  // console.log('productPaths:motor', productPaths);
  return {
    props: {
      ...getPurchaseFlowHeroForms(productCategorySlug).props,
      productPaths,
    },
  };
}

export default MotorInsurance;
